import React, { useState } from 'react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';

const ImportRecordsModal = ({
  show,
  onHide,
  onImport,
  canImportAttendance,
  error,
  setError
}) => {
  const [csvData, setCsvData] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);

  const downloadTemplate = () => {
    // Create sample data for the attendance records template with DD/MM/YYYY format
    const templateData = [
      {
        emp_code: 'EMP001',
        att_date: '15/01/2024',
        status_code: 'P',
        att_value: '1.00',
        in_time: '09:00:00',
        out_time: '17:00:00',
        work_hours: '8.00',
        remarks: 'Regular working day'
      },
      {
        emp_code: 'EMP002',
        att_date: '15/01/2024',
        status_code: 'A',
        att_value: '0.00',
        in_time: '',
        out_time: '',
        work_hours: '0.00',
        remarks: 'Absent without leave'
      },
      {
        emp_code: 'EMP003',
        att_date: '15/01/2024',
        status_code: 'CL',
        att_value: '1.00',
        in_time: '',
        out_time: '',
        work_hours: '0.00',
        remarks: 'Casual leave approved'
      },
      {
        emp_code: 'EMP001',
        att_date: '16/01/2024',
        status_code: 'PA',
        att_value: '0.50',
        in_time: '09:00:00',
        out_time: '13:00:00',
        work_hours: '4.00',
        remarks: 'Half day - personal work'
      },
      {
        emp_code: 'EMP004',
        att_date: '17/01/2024',
        status_code: 'EL',
        att_value: '1.00',
        in_time: '',
        out_time: '',
        work_hours: '0.00',
        remarks: 'Earned leave'
      }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths for better readability
    const wscols = [
      { wch: 10 }, // emp_code
      { wch: 12 }, // att_date
      { wch: 10 }, // status_code
      { wch: 10 }, // att_value
      { wch: 10 }, // in_time
      { wch: 10 }, // out_time
      { wch: 12 }, // work_hours
      { wch: 25 }  // remarks
    ];
    ws['!cols'] = wscols;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Records");

    // Add instructions sheet
    const instructions = [
      ['ATTENDANCE RECORDS IMPORT TEMPLATE - INSTRUCTIONS'],
      [''],
      ['REQUIRED FIELDS:'],
      ['emp_code - Employee code (must exist in system)'],
      ['att_date - Attendance date in DD/MM/YYYY format'],
      [''],
      ['OPTIONAL FIELDS:'],
      ['status_code - Attendance status code (P, A, CL, EL, PA, LOP, LO)'],
      ['att_value - Attendance value (0.00 to 1.00, decimal)'],
      ['in_time - Check-in time in HH:MM:SS format'],
      ['out_time - Check-out time in HH:MM:SS format'],
      ['work_hours - Total work hours (decimal)'],
      ['remarks - Any additional notes'],
      [''],
      ['STATUS CODES REFERENCE:'],
      ['P - Present (att_value: 1.00)'],
      ['A - Absent (att_value: 0.00)'],
      ['CL - Casual Leave (att_value: 1.00)'],
      ['EL - Earned Leave (att_value: 1.00)'],
      ['PA - Half Day Present/Absent (att_value: 0.50)'],
      ['LOP - Loss of Pay (att_value: 0.00)'],
      ['LO - Lay Off (att_value: 0.50)'],
      [''],
      ['DATE FORMAT:'],
      ['• Use DD/MM/YYYY format (e.g., 15/01/2024)'],
      ['• Day: 01-31'],
      ['• Month: 01-12'],
      ['• Year: 2024, 2025, etc.'],
      [''],
      ['TIME FORMAT:'],
      ['• Use 24-hour format HH:MM:SS'],
      ['• Example: 09:00:00, 13:30:00, 17:45:00'],
      [''],
      ['IMPORTANT NOTES:'],
      ['1. Remove this instructions sheet before uploading'],
      ['2. Keep only the data sheet with header row'],
      ['3. Dates must be in DD/MM/YYYY format'],
      ['4. Times must be in 24-hour HH:MM:SS format'],
      ['5. If status_code is provided, att_value can be auto-calculated'],
      ['6. For empty time fields, leave cell blank'],
      ['7. Employee code must exist in the system']
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    const instructionCols = [
      { wch: 60 } // Wide column for instructions
    ];
    ws2['!cols'] = instructionCols;
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    // Add validation sheet
    const validation = [
      ['DATA VALIDATION RULES'],
      [''],
      ['Field', 'Format', 'Required', 'Example'],
      ['emp_code', 'Text', 'Yes', 'EMP001'],
      ['att_date', 'DD/MM/YYYY', 'Yes', '15/01/2024'],
      ['status_code', 'P/A/CL/EL/PA/LOP/LO', 'No', 'P'],
      ['att_value', 'Decimal (0.00-1.00)', 'No', '1.00'],
      ['in_time', 'HH:MM:SS (24h)', 'No', '09:00:00'],
      ['out_time', 'HH:MM:SS (24h)', 'No', '17:30:00'],
      ['work_hours', 'Decimal', 'No', '8.50'],
      ['remarks', 'Text', 'No', 'Any notes']
    ];

    const ws3 = XLSX.utils.aoa_to_sheet(validation);
    const validationCols = [
      { wch: 15 },
      { wch: 20 },
      { wch: 10 },
      { wch: 15 }
    ];
    ws3['!cols'] = validationCols;
    XLSX.utils.book_append_sheet(wb, ws3, "Validation Rules");

    // Save file
    XLSX.writeFile(wb, 'attendance_records_template.xlsx');
  };

  const handleFileUpload = (event) => {
    if (!canImportAttendance()) {
      alert('You do not have permission to import attendance records');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // Check file extension
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (isExcel) {
      // Handle Excel files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get first sheet
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          // Convert to JSON with date handling
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
            raw: false, // Get formatted values
            dateNF: 'dd/mm/yyyy' // Date format
          });

          if (jsonData && jsonData.length > 0) {
            setCsvData(jsonData);
          } else {
            setError('No valid data found in Excel file');
          }
        } catch (error) {
          setError('Error reading Excel file: ' + error.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Handle CSV files
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            setCsvData(results.data);
          } else {
            setError('No valid data found in CSV file');
          }
        },
        error: (error) => {
          setError('Error parsing CSV file: ' + error.message);
        }
      });
    }
  };

  const handleImport = async () => {
    if (!canImportAttendance()) {
      alert('You do not have permission to import attendance records');
      return;
    }

    if (!csvData.length) {
      setError('No data to import');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    setError('');

    try {
      await onImport(csvData, setImportProgress);
      onHide();
      setCsvData([]);
    } catch (error) {
      setError('Import failed: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setCsvData([]);
    setImportProgress(0);
    setIsImporting(false);
    onHide();
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header bg-success text-white">
            <h5 className="modal-title">
              <i className="bi bi-upload me-2"></i>
              Import Attendance from CSV/Excel
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={isImporting}
            ></button>
          </div>

          <div className="modal-body">
            <div className="row g-4">
              <div className="col-12">
                <div className="alert alert-info">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="alert-heading">
                        <i className="bi bi-info-circle me-2"></i>
                        File Format Requirements
                      </h6>
                      <p className="mb-1">Your file should include these columns:</p>
                      <ul className="mb-0 small">
                        <li><strong>emp_code</strong> (required) - Employee code</li>
                        <li><strong>att_date</strong> (required) - Date in <span className="text-danger">DD/MM/YYYY</span> format</li>
                        <li><strong>status_code</strong> - Attendance status code</li>
                        <li><strong>att_value</strong> - Attendance value (0.00-1.00)</li>
                        <li><strong>in_time</strong> - Check-in time (HH:MM:SS)</li>
                        <li><strong>out_time</strong> - Check-out time (HH:MM:SS)</li>
                        <li><strong>work_hours</strong> - Total work hours</li>
                        <li><strong>remarks</strong> - Additional notes</li>
                      </ul>
                    </div>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={downloadTemplate}
                      title="Download Excel Template"
                    >
                      <i className="bi bi-download me-1"></i>
                      Template
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card border-success">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Download Template First (Important!)
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <h6 className="text-success">
                          <i className="bi bi-calendar-check me-2"></i>
                          Use DD/MM/YYYY Date Format
                        </h6>
                        <p className="mb-2">
                          <strong>Step 1:</strong> Download the Excel template with correct date format
                        </p>
                        <p className="mb-2">
                          <strong>Step 2:</strong> Fill your data using <span className="text-danger">DD/MM/YYYY</span> format
                        </p>
                        <p className="mb-0 text-muted small">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Wrong date format will cause import errors
                        </p>
                      </div>
                      <div className="col-md-4 text-end">
                        <button
                          className="btn btn-success btn-lg"
                          onClick={downloadTemplate}
                        >
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          Download Template
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="bi bi-upload me-2"></i>
                      Upload Your File
                    </h6>
                  </div>
                  <div className="card-body">
                    <label className="form-label">Select File *</label>
                    <div className="input-group">
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={isImporting}
                      />
                      <button
                        className="btn btn-outline-success"
                        type="button"
                        onClick={downloadTemplate}
                        title="Download Template"
                        disabled={isImporting}
                      >
                        <i className="bi bi-download me-1"></i>
                        Template
                      </button>
                    </div>
                    <div className="form-text">
                      Supports CSV, XLSX, XLS files. Maximum file size: 10MB
                    </div>

                    <div className="mt-3 alert alert-warning">
                      <i className="bi bi-calendar me-2"></i>
                      <strong>Important:</strong> Dates must be in <span className="text-danger">DD/MM/YYYY</span> format (e.g., 15/01/2024)
                    </div>
                  </div>
                </div>
              </div>

              {csvData.length > 0 && (
                <div className="col-12">
                  <div className="card bg-light">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="card-title mb-0">
                          <i className="bi bi-table me-2"></i>
                          Preview ({csvData.length} records found)
                        </h6>
                        <span className="badge bg-success">
                          <i className="bi bi-calendar me-1"></i>
                          DD/MM/YYYY Format
                        </span>
                      </div>
                      <div className="table-responsive" style={{ maxHeight: '250px' }}>
                        <table className="table table-sm table-bordered">
                          <thead className="bg-light">
                            <tr>
                              {Object.keys(csvData[0]).map(key => (
                                <th key={key} className="small">
                                  {key === 'att_date' ? (
                                    <span className="text-danger">{key}*</span>
                                  ) : key === 'emp_code' ? (
                                    <span className="text-danger">{key}*</span>
                                  ) : (
                                    key
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {csvData.slice(0, 5).map((row, index) => (
                              <tr key={index}>
                                {Object.entries(row).map(([key, value], i) => (
                                  <td
                                    key={i}
                                    className="small"
                                    style={{
                                      color: key === 'att_date' && !value ? 'red' : 'inherit',
                                      fontWeight: key === 'att_date' ? 'bold' : 'normal'
                                    }}
                                  >
                                    {key === 'att_date' && !value ? (
                                      <span className="text-danger">Required!</span>
                                    ) : (
                                      value || ''
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {csvData.length > 5 && (
                        <div className="text-center small text-muted mt-2">
                          <i className="bi bi-arrow-down"></i>
                          ... and {csvData.length - 5} more records
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {isImporting && (
                <div className="col-12">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Importing Records...
                      </h6>
                      <div className="progress mb-2">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                          style={{ width: `${importProgress}%` }}
                        >
                          {importProgress}%
                        </div>
                      </div>
                      <small className="text-muted">
                        Processing your attendance records. Please wait...
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isImporting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={downloadTemplate}
              disabled={isImporting}
            >
              <i className="bi bi-download me-2"></i>
              Get Template
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleImport}
              disabled={!csvData.length || isImporting}
            >
              {isImporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Importing...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  Start Import
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportRecordsModal;